import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormWatch } from 'react-hook-form';

import useFileFeasibilityReport from 'api/hooks/useFileFeasibilityReport';
import Button from 'components/Button';
import { FeasibilityReportCard } from 'components/FeasibilityReportCard';
import useUser from 'api/hooks/useUser';
import { Mixpanel } from 'mixpanel';
import { BriefProps } from 'utils/types';
import { IFeasibilityCheck } from 'features/FeasibilityCheck';
import { cn } from 'utils/cn';

import {
  getAdvantagesText,
  getBadgeType,
  getPlantSetupCheck,
  getProductRecirculationBadgeText,
  getProductRecirculationText,
  getProductStirringBadgeText,
  getProductStirringCombination,
  getProductStirringText,
  getPumpTypeBadgeText,
  getPumpTypeText,
} from './utils/reportHelper';

export interface ITechnicalData {
  product: string;
  annualConsumption: string;
  productPackaging: string;
  pumpType: string;
  rentalPump: string;
  linesMore: string;
  linesLess: string;
  valves: string;
  risk: string;
  productBefore: string;
  stirredTank: string;
  productRecirculation: string;
  productWithdrawel: string;
  numberFtl: string;
  timeDeliveries: string;
  tankBottom: string;
  expectedProductConsumption: string;
}
interface Props {
  setActiveTab: (value: number) => void;
  briefData: BriefProps[];
  formWatch: UseFormWatch<IFeasibilityCheck>;
  isPDF?: boolean;
}

const ReportFeasibility: FC<Props> = ({
  setActiveTab,
  briefData,
  formWatch,
  isPDF,
}) => {
  const { t } = useTranslation();
  const { data } = useFileFeasibilityReport();
  const { data: user } = useUser();

  const applicationData = formWatch('application');
  const plantSetUpData = formWatch('plant');
  const chemicalData = formWatch('chemical');
  const technicalData = formWatch('technical');
  const economicData = formWatch('economic');

  useEffect(() => {
    if (user) {
      Mixpanel.track('Feasibility Check Step 6', {
        role: user?.role?.name,
        organization: user?.organization,
      });
    }
  }, [user]);

  if (!data) {
    return null;
  }
    
  return (
    <div className={cn(
      'border border-primitiveGrey p-8 rounded-2xl',
      { 'md:py-6 md:px-4': !isPDF },
    )}>
      {!isPDF && (
        <div className="text-black text-xl font-bold mb-8">{t('Report')}</div>
      )}
      {applicationData?.product?.value && (
        <>
          <div>
            <div className="text-black text-lg font-bold mb-6">
              {data?.[15]}
            </div>
            <FeasibilityReportCard
              isPDF={isPDF}
              bageText={
                applicationData?.industry?.value === t('other')
                || applicationData?.subIndustry?.value === t('other')
                || applicationData?.apps?.value === t('other')
                || applicationData?.product?.value === t('other')
                  ? data?.[4]
                  : data?.[2]
              }
              title={data?.[16]}
              description={
                applicationData?.industry?.value === t('other')
                || applicationData?.subIndustry?.value === t('other')
                || applicationData?.apps?.value === t('other')
                || applicationData?.product?.value === t('other')
                  ? data?.[1] : data?.[0]
              }
              badgeType={getBadgeType(
                applicationData?.industry?.value === t('other')
                || applicationData?.subIndustry?.value === t('other')
                || applicationData?.apps?.value === t('other')
                || applicationData?.product?.value === t('other')
                  ? data?.[4] : data?.[2],
                data,
              )}
            />
            <FeasibilityReportCard
              isPDF={isPDF}
              bageText={
                applicationData?.industry?.value === t('other') ? data?.[3] : data?.[2]
              }
              title={data?.[17]}
              description={
                applicationData?.industry?.value === t('other') ? data?.[1] : (getAdvantagesText({
                  brief: briefData,
                  product: applicationData?.product?.value,
                  industry: applicationData?.industry?.value || '',
                  subIndustry: applicationData?.subIndustry?.value || '',
                  apps: applicationData?.apps?.value || '',
                  t,
                })?.map((item, i) => (
                  <div
                    key={`${item}+ ${i}`}
                    className="flex items-center mb-2"
                  >
                    <span className="flex-shrink-0 w-[6px] h-[6px] mr-2 bg-[#007AC1] rounded-full"/>
                    {item}
                  </div>
                )))
              }
              badgeType={getBadgeType(
                applicationData?.industry?.value === t('other') ? data?.[3] : data?.[2],
                data,
              )}
            />
          </div>
          <div className="w-full h-px bg-[#E2E2E2] mb-8 mt-8" />
        </>
      )}
      {economicData?.omya?.value && (
        <>
          <div>
            <div className="text-black text-lg font-bold mb-6">
              {data?.[18]}
            </div>
            <FeasibilityReportCard
              isPDF={isPDF}
              bageText={
                chemicalData.chemicalFeasibility === t('yes')
                  ? data[2]
                  : data[4]
              }
              title={data?.[19]}
              description={
                chemicalData.chemicalFeasibility === t('yes')
                  ? data[7]
                  : data[8]
              }
              badgeType={getBadgeType(
                chemicalData.chemicalFeasibility === t('yes')
                  ? data[2]
                  : data[4],
                data,
              )}
            />
            {economicData?.omya?.value !== 'Omya Neutrasorb Mag - Mg(OH)₂' &&
              plantSetUpData?.processSteps?.length &&
              getPlantSetupCheck(plantSetUpData) && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={data[3]}
                title={data?.[20]}
                description={data[33]}
                badgeType={getBadgeType(data[3], data)}
              />
            )}
            {applicationData?.industry?.value?.includes(t('metal')) &&
              [data[54], data[55], data[56]].includes(
                economicData.omya.value,
              ) &&
              chemicalData?.sulfate?.value === t('yes') && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={data[3]}
                title={data?.[20]}
                description={data[34]}
                badgeType={getBadgeType(data[3], data)}
              />
            )}
            {applicationData?.industry?.value?.includes(t('potatoSegment')) &&
              [data[57]].includes(economicData.omya.value) && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={data[3]}
                title={data?.[20]}
                description={data[35]}
                badgeType={getBadgeType(data[3], data)}
              />
            )}
          </div>
          <div className="w-full h-px bg-[#E2E2E2] mb-8 mt-8" />
        </>
      )}
      {technicalData?.product?.value && (
        <>
          <div>
            <div className="text-black text-lg font-bold mb-6">
              {data?.[21]}
            </div>
            {technicalData.productPackaging?.value === t('bulk') && getProductStirringCombination(technicalData, t) && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={getProductStirringBadgeText(
                  getProductStirringCombination(technicalData, t),
                  data,
                )}
                title={`${data?.[22]} - ${data?.[25]}`}
                description={getProductStirringText(
                  getProductStirringCombination(technicalData, t),
                  data,
                )}
                badgeType={getBadgeType(
                  getProductStirringBadgeText(
                    getProductStirringCombination(technicalData, t),
                    data,
                  ),
                  data,
                )}
              />
            )}
            {technicalData.productPackaging?.value === t('bulk') &&
              getProductRecirculationBadgeText(
                getProductStirringCombination(technicalData, t),
                data,
              ) && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={getProductRecirculationBadgeText(
                  getProductStirringCombination(technicalData, t),
                  data,
                )}
                title={`${data?.[22]} - ${t('productRecirculation')}`}
                description={getProductRecirculationText(
                  getProductStirringCombination(technicalData, t),
                  data,
                )}
                badgeType={getBadgeType(
                  getProductRecirculationBadgeText(
                    getProductStirringCombination(technicalData, t),
                    data,
                  ),
                  data,
                )}
              />
            )}
            {getPumpTypeBadgeText(technicalData.pumpType?.value || '', data, t) && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={getPumpTypeBadgeText(technicalData.pumpType?.value || '', data, t)}
                title={data?.[26]}
                description={getPumpTypeText(technicalData.pumpType?.value || '', data, t)}
                badgeType={getBadgeType(
                  getPumpTypeBadgeText(technicalData.pumpType?.value || '', data, t),
                  data,
                )}
              />
            )}
            {technicalData.rentalPump?.value && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={
                  technicalData.rentalPump.value === t('no') ? data?.[2] : data?.[5]
                }
                title={data?.[27]}
                description={
                  technicalData.rentalPump.value === t('no') ? '' : data?.[10]
                }
                badgeType={getBadgeType(
                  technicalData.rentalPump.value === t('no') ? data?.[2] : data?.[5],
                  data,
                )}
              />
            )}
            {technicalData.linesLess?.value && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={
                  technicalData.linesLess.value === t('yes') ? data?.[2] : data?.[5]
                }
                title={`${data?.[24]} - ${data?.[28]}`}
                description={
                  technicalData.linesLess.value === t('yes') ? data?.[41] : data?.[42]
                }
                badgeType={getBadgeType(
                  technicalData.linesLess.value === t('yes') ? data?.[2] : data?.[5],
                  data,
                )}
              />
            )}
            {technicalData.valves?.value && <FeasibilityReportCard
              isPDF={isPDF}
              bageText={
                technicalData.valves.value === t('yes') ? data?.[2] : data?.[5]
              }
              title={`${data?.[24]} - ${data?.[29]}`}
              description={
                technicalData.valves.value === t('yes') ? data?.[45] : data?.[46]
              }
              badgeType={getBadgeType(
                technicalData.valves.value === t('yes') ? data?.[2] : data?.[5],
                data,
              )}
            />}
            {technicalData.linesMore?.value && <FeasibilityReportCard
              isPDF={isPDF}
              bageText={
                technicalData.linesMore.value === t('yes') ? data?.[2] : data?.[5]
              }
              title={`${data?.[24]} - ${data?.[30]}`}
              description={
                technicalData.linesMore.value === t('yes') ? data?.[45] : data?.[44]
              }
              badgeType={getBadgeType(
                technicalData.linesMore.value === t('yes') ? data?.[2] : data?.[5],
                data,
              )}
            />}
            {technicalData.productWithdrawel?.value === t('viaTop') &&
              technicalData.productPackaging?.value === t('bulk') && (
              <FeasibilityReportCard
                isPDF={isPDF}
                bageText={data?.[3]}
                description={data[11]}
                badgeType={getBadgeType(data?.[3], data)}
              />
            )}
            {technicalData.risk?.value && <FeasibilityReportCard
              isPDF={isPDF}
              bageText={technicalData.risk.value === t('no') ? data?.[2] : data?.[5]}
              title={data?.[31]}
              description={
                technicalData.risk.value === t('no')
                  ? ''
                  : technicalData.productPackaging?.value === t('bulk')
                    ? data[47]
                    : data[48]
              }
              badgeType={getBadgeType(
                technicalData.risk.value === t('no') ? data?.[2] : data?.[5],
                data,
              )}
            />}
            <FeasibilityReportCard
              isPDF={isPDF}
              bageText={
                technicalData.productBefore?.value.split(' - ')?.[1] === technicalData.product?.value.split(' - ')?.[1]
                  ? data?.[2]
                  : data?.[5]
              }
              title={data?.[32]}
              description={
                technicalData.productBefore?.value.split(' - ')?.[1] !== technicalData.product?.value.split(' - ')?.[1] && data?.[13]
              }
              badgeType={getBadgeType(
                technicalData.productBefore?.value.split(' - ')?.[1] === technicalData.product?.value.split(' - ')?.[1]
                  ? data?.[2]
                  : data?.[5],
                data,
              )}
            />
          </div>
          <div className="w-full h-px bg-[#E2E2E2] mb-8 mt-8" />
        </>
      )}
      {!isPDF && (
        <div className="mt-8 flex justify-center md:mt-6">
          <Button
            onClick={() => setActiveTab(5)}
            className="mr-4"
            btnType="secondary"
          >
            {t('back')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReportFeasibility;
